/**
 * Who counts as a team member on Team settings (employees + admins, not sellers).
 */

import { canInviteStaff } from "./staffAccess";

export function isAccountOwner(user, enterprise) {
  return !!(user?.uid && enterprise?.uid && user.uid === enterprise.uid);
}

/**
 * Provisioned staff who never completed first sign-in.
 * Requires an explicit `hasSignedIn === false` from the API so a frontend
 * deploy ahead of the backend does not mark everyone inactive.
 */
export function isInviteInactive(user, { ownerUid } = {}) {
  if (!user || user.staffRevoked) return false;
  if (ownerUid && user.uid === ownerUid) return false;
  if (user.hasSignedIn !== false) return false;
  return true;
}

export function canResendTeamInvite(user, { ownerUid } = {}) {
  return !!(isInviteInactive(user, { ownerUid }) && user.invitationId);
}

/** Employees list status: Active | Invited | Inactive | Revoked */
export function memberListStatus(user, { ownerUid } = {}) {
  if (user?.staffRevoked) return "Revoked";
  if (ownerUid && user?.uid === ownerUid) return "Active";
  if (user?.hasSignedIn === true) return "Active";
  if (isInviteInactive(user, { ownerUid })) {
    return user.inviteLinkExpired === false ? "Invited" : "Inactive";
  }
  return "Active";
}

/** Full admins (not custom-scoped staff). */
export function isFullAdminUser(user) {
  if (!user) return false;
  if (user.staffRevoked) return false;
  if (user.staffAccess?.mode === "custom") return false;
  if (user.staffAccess?.mode === "full_admin") return true;
  const roles = user.roles || [];
  if (roles.includes("admin") || roles.includes("super_admin")) return true;
  // Legacy enterprise admins without a staffAccess record.
  if (user.isEnterpriseAdmin && !user.invitedStaff) return true;
  return false;
}

export function isEmployeeUser(user) {
  if (!user?.uid) return false;
  if (user.invitedStaff) return true;
  if (user.staffRevoked) return true;
  if (user.staffAccess?.mode) return true;
  if (user.locationId) return true;
  const roles = user.roles || [];
  return roles.includes("member");
}

/**
 * Staff/admin markers (ignores archived). Used to keep team accounts off
 * Sellers even when a staff user was archived.
 *
 * Bare roles:["member"] without staff markers is NOT team — public signup
 * historically defaulted to member and those users are sellers.
 */
function hasTeamMemberMarkers(user) {
  if (!user) return false;
  if (user.invitedSharedAccess) return false;
  if (user.staffRevoked) return true;
  if (user.invitedStaff) return true;
  if (user.registerStaff || user.role === "register") return true;
  if (user.isEnterpriseAdmin) return true;
  if (user.locationId) return true;
  if (user.staffAccess?.mode) return true;
  const roles = user.roles || [];
  return (
    roles.includes("admin") ||
    roles.includes("super_admin") ||
    roles.includes("register")
  );
}

/**
 * Everyone who belongs on Team / Employee activity — employees + admins.
 * Same rules as Employee Activity so anyone listed there also appears here.
 * Excludes sellers (no admin/staff/member markers).
 */
export function isTeamMemberUser(user) {
  if (!user?.uid) return false;
  if (user.archived) return false;
  return hasTeamMemberMarkers(user);
}

/** Sellers / invoicing users — not staff, admins, employees, or shared-access helpers. */
export function isSellerAccountUser(user) {
  if (!user?.uid) return false;
  if (user.invitedSharedAccess) return false;
  return !hasTeamMemberMarkers(user);
}

/** Register only teammate: PIN, no email or Firebase login. */
export function isPinOnlyStaff(user) {
  return !!(user && (user.registerStaff || user.role === "register"));
}

export function employeeAccessLabel(userOrInv, { ownerUid } = {}) {
  if (ownerUid && userOrInv?.uid === ownerUid) return "Owner";
  if (userOrInv?.staffRevoked) return "Revoked";
  if (isPinOnlyStaff(userOrInv)) {
    const access = userOrInv.registerStoreAccess;
    const n =
      access?.mode === "list" && Array.isArray(access.storeIds)
        ? access.storeIds.length
        : 0;
    return n
      ? `Register, ${n} store${n === 1 ? "" : "s"}`
      : "Register, all stores";
  }
  if (
    userOrInv?.staffAccess?.preset === "fulfiller_ops" ||
    (userOrInv?.roles || []).includes("fulfiller_ops")
  ) {
    const perms = Array.isArray(userOrInv?.staffAccess?.permissions)
      ? userOrInv.staffAccess.permissions
      : [];
    const count = perms.filter(
      (id) =>
        !perms.some(
          (other) => other !== id && String(other).startsWith(`${id}/`)
        )
    ).length;
    return count ? `Member (${count})` : "Member";
  }
  const mode = userOrInv?.staffAccess?.mode;
  if (mode === "custom") {
    const perms = Array.isArray(userOrInv?.staffAccess?.permissions)
      ? userOrInv.staffAccess.permissions
      : [];
    // Count leaf grants only (ignore parent ids like `orders-admin`).
    const count = perms.filter(
      (id) =>
        !perms.some(
          (other) => other !== id && String(other).startsWith(`${id}/`)
        )
    ).length;
    return count ? `Custom (${count})` : "Custom (none)";
  }
  if (mode === "full_admin") return "Full admin";
  if ((userOrInv?.roles || []).includes("admin")) return "Full admin";
  if ((userOrInv?.roles || []).includes("member")) return "Member";
  if (userOrInv?.isEnterpriseAdmin) return "Full admin";
  return userOrInv?.role || "—";
}

/**
 * Who may remove access for a team member.
 * Account owner is never revocable. Full admins can only be revoked by the owner.
 */
export function canRevokeTeamUser(actor, target, enterprise) {
  if (!actor?.uid || !target?.uid) return false;
  if (actor.uid === target.uid) return false;
  if (isAccountOwner(target, enterprise)) return false;
  if (!canInviteStaff(actor)) return false;
  if (isFullAdminUser(target)) {
    return isAccountOwner(actor, enterprise);
  }
  return true;
}

/**
 * Full admins (and owners) can edit employee accounts — including their own
 * and other full admins. The account owner record stays locked.
 */
export function canEditTeamUser(actor, target, enterprise) {
  if (!actor?.uid || !target?.uid) return false;
  if (isAccountOwner(target, enterprise)) return false;
  if (!canInviteStaff(actor)) return false;
  return true;
}

/**
 * Admins + employees may see who QC'd / shipped / picked / printed.
 * Sellers see the same events without employee names.
 */
export function canViewStaffActorNames(user) {
  if (!user) return false;
  if (user.invitedStaff || user.staffAccess?.mode) return true;
  return !!user.isEnterpriseAdmin;
}

/** Prefer a stored employee label from an orderItemsHistory event. */
export function staffActorLabelFromHistoryEvent(event) {
  if (!event || typeof event !== "object") return null;
  const candidates = [
    event.qcPassedByName,
    event.qcFailedByName,
    event.pickedByName,
    event.printedByName,
    event.actorName,
  ];
  for (const raw of candidates) {
    const name = String(raw || "").trim();
    if (!name) continue;
    // Never surface a bare Firebase UID as a name.
    if (/^[A-Za-z0-9]{20,36}$/.test(name) && !name.includes("@") && !name.includes(" ")) {
      continue;
    }
    return name;
  }
  return null;
}
